import * as React from 'react';
import { Flex, FlexDirection } from 'src/components/Flex/Flex';
import { FlexItem } from 'src/components/FlexItem/FlexItem';
import styled from 'styled-components';
import { Container } from '../../components/Container/Container';
import { H1, H2 } from 'src/components/Elements/Elements';
import { PageView } from 'src/components/GoogleAnalytics/GoogleAnalytics';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResponsiveSize, ErrorMessages, FlexPosition } from 'src/components/Globals/Globals';
import { Loader } from '../../components/Loader/Loader';
import { AppConsumer } from 'src/contexts/Context';
import { FileTable } from '../../components/FileTable/FileTable';
import { RelatedSolutionsTable } from '../../components/RelatedSolutionsTable/RelatedSolutionsTable';
import { formatDate } from '../../tools/CommonFunctions';
import { BackButton } from '../../components/Button/Button';
import { Link } from 'react-router-dom';
import { CollapsibleContainer } from '../../components/CollapsibleContainer/CollapsibleContainer';
import * as ReactTooltip from 'react-tooltip';
import { Solution } from 'src/models/Solution';

const OfferingImageWrapper = styled(Flex)`
  width: 100%;
  margin: 1em 0;
  overflow: auto;

  height: 40vh;
  min-height: 40vh;
  max-height: 40vh;

  & img {
    height: 100% !important;
    width: auto !important;
    max-width: 100%;
    object-fit: contain;
    overflow: hidden;
  }
`;

// Promotion cards
const PromoCard = styled(FlexItem)`
  flex-direction: column;
  padding: 0 1em;
`;

const PromoLogo = styled.span`
  text-align: center;
  width: 100%;
`;

const PromoTitle = styled.p`
  text-transform: uppercase;
  display: block;
`;

const PromoDescription = styled.p`
  text-align: left;
  line-height: 1.6;
  word-break: break-word;
  white-space: pre-wrap;
`;

const PromoWrapper = styled(Flex)`
  @media (max-width: ${ResponsiveSize.laptop}) {
    flex-direction: column;
  }

  margin-bottom: 1.5em;
`;

// Table that displays info about the solution
const InfoContainer = styled(Flex)`
  padding: 2em;
  margin-bottom: 2em;
  @media (max-width: ${ResponsiveSize.laptop}) {
    flex-direction: column;
    padding: 2em 1em;
  }
`;

const FileTableContainer = styled(Flex)`
  margin-bottom: 2em;
`;

const RelatedSolutionsTableContainer = styled(Flex)`
  margin-bottom: 2em;
`;

const InfoTable = styled.table`
  text-align: left;
  font-size: 14px;
  line-height: 2;

  & > tbody {
    vertical-align: top;
  }

  @media (max-width: ${ResponsiveSize.laptop}) {
    flex-direction: column;
    font-size: 1.2em;
  }

  @media (max-width: ${ResponsiveSize.tablet}) {
    font-size: 1em;
  }
`;

const ViewCountContainer = styled.p`
  text-align: center;
`;

interface IOfferingProps {
  /** Gets a solution with the corresponding ID. Received as a prop from parent */
  setSelectedSolution(id: string): Solution | null;
  incrementSolutionView(id: string): void;
  waitForSolutionsToBeLoaded(): Promise<any>;
  parseSolutionImageHtml(html: string): Promise<string>;
  getSolutionViews(id: string, dateRange?: number): Promise<string>;
}

interface IOfferingState {
  loading: boolean;
  solutionImageHtml?: string;
  totalViews?: number;
  recentViews?: number;
  solutionId?: string;
}

export class Offering extends React.Component<IOfferingProps, IOfferingState> {
  constructor(props: IOfferingProps) {
    super(props);

    this.state = {
      loading: true
    };
  }

  // Get solution data on page load.
  public componentWillMount() {
    window.scrollTo(0, 0);
    this.setState({ solutionId: this.getIdFromUrlParameters() }, () =>
      this.initSolution(this.state.solutionId || '')
    );
  }

  public componentWillReceiveProps() {
    if (this.state.solutionId !== this.getIdFromUrlParameters()) {
      this.setState({ solutionId: this.getIdFromUrlParameters() }, () =>
        this.initSolution(this.state.solutionId || '')
      );
    }
  }

  public getIdFromUrlParameters(): string {
    const splittedUrl = window.location.pathname.split('/');
    return splittedUrl[splittedUrl.length - 1];
  }

  /** Gets the solution ID from url and sets the state */
  public initSolution(solutionId: string) {
    this.setState({ solutionId });
    // Waits for solutions to be loaded
    this.props.waitForSolutionsToBeLoaded().then((value: any) => {
      this.props
        .getSolutionViews(solutionId)
        .then(totalViews => this.setState({ totalViews: parseInt(totalViews, 10) + 1 }));
      this.props
        .getSolutionViews(solutionId, 90)
        .then(recentViews => this.setState({ recentViews: parseInt(recentViews, 10) + 1 }));
      // Sets the selected solution by using the ID in url
      const selectedSolution = this.props.setSelectedSolution(solutionId);
      // Increment view count of the solution by 1
      this.props.incrementSolutionView(solutionId);
      // If solution has images, they need to be processed. Images are in the format of img -tags.
      if (selectedSolution && selectedSolution.solutionImage) {
        this.props
          .parseSolutionImageHtml(selectedSolution.solutionImage)
          .then(solutionImageHtml => this.setState({ solutionImageHtml }));
      }
      this.setState({ loading: false });
      if (selectedSolution !== null) {
        PageView(selectedSolution.solutionName);
      } else {
        PageView();
      }
    });
  }

  public render() {
    return (
      <AppConsumer>
        {({ state, apiActions }) => (
          <Container style={{ flexGrow: 1 }}>
            {state.selectedSolution === null ||
            state.selectedSolution === undefined ||
            this.state.loading ? (
              <Flex style={{ paddingTop: '2em' }}>
                <Loader
                  text="Loading solution..."
                  failed={
                    (!state.selectedSolution && !this.state.loading) || state.solutionLoadError
                  }
                  failedText={
                    state.solutionLoadError
                      ? ErrorMessages.networkError
                      : ErrorMessages.solutionNotFoundError
                  }
                />
              </Flex>
            ) : (
              <Flex direction={FlexDirection.column}>
                <Flex alignHorizontal={FlexPosition.center} style={{ marginTop: '1em' }}>
                  <Link to="/">
                    <BackButton>&laquo; Return to front page</BackButton>
                  </Link>
                </Flex>
                <OfferingImageWrapper
                  alignHorizontal={FlexPosition.center}
                  dangerouslySetInnerHTML={{ __html: this.state.solutionImageHtml || '' }}
                />

                <H1 style={{ textAlign: 'center', marginBottom: '1.5em' }}>
                  {state.selectedSolution.solutionName}
                </H1>

                <PromoWrapper>
                  {state.selectedSolution.theChallenge && (
                    <PromoCard>
                      <PromoLogo>
                        <FontAwesomeIcon icon="question-circle" style={{ fontSize: '3em' }} />
                        <PromoTitle>the challenge</PromoTitle>
                      </PromoLogo>
                      <PromoDescription>{state.selectedSolution.theChallenge}</PromoDescription>
                    </PromoCard>
                  )}

                  {state.selectedSolution.ourSolution && (
                    <PromoCard>
                      <PromoLogo>
                        <FontAwesomeIcon icon="check-circle" style={{ fontSize: '3em' }} />
                        <PromoTitle>our solution</PromoTitle>
                      </PromoLogo>
                      <PromoDescription>{state.selectedSolution.ourSolution}</PromoDescription>
                    </PromoCard>
                  )}

                  {state.selectedSolution.benefits && (
                    <PromoCard>
                      <PromoLogo>
                        <FontAwesomeIcon icon="smile" style={{ fontSize: '3em' }} />
                        <PromoTitle>benefits</PromoTitle>
                      </PromoLogo>
                      <PromoDescription>{state.selectedSolution.benefits}</PromoDescription>
                    </PromoCard>
                  )}

                  {state.selectedSolution.targetCustomer && (
                    <PromoCard>
                      <PromoLogo>
                        <FontAwesomeIcon icon="user-circle" style={{ fontSize: '3em' }} />
                        <PromoTitle>target customer</PromoTitle>
                      </PromoLogo>
                      <PromoDescription>{state.selectedSolution.targetCustomer}</PromoDescription>
                    </PromoCard>
                  )}
                </PromoWrapper>

                <hr style={{ width: '100%' }} />

                <InfoContainer>
                  <FlexItem>
                    <InfoTable style={{ width: '100%' }}>
                      <tbody>
                        <tr>
                          <th style={{ width: '30%' }}>Product Manager</th>
                          <td>{state.selectedSolution.productManager}</td>
                        </tr>
                        <tr>
                          <th>Industry</th>
                          <td>
                            <CollapsibleContainer
                              text={state.selectedSolution.industry.join(', ')}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Business Line</th>
                          <td>
                            <CollapsibleContainer
                              text={state.selectedSolution.businessLine.join(', ')}
                            />
                          </td>
                        </tr>
                        {state.selectedSolution.offeringType && (
                          <tr>
                            <th>Offering Type</th>
                            <td>
                              <CollapsibleContainer text={state.selectedSolution.offeringType} />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </InfoTable>
                  </FlexItem>
                  <FlexItem>
                    <InfoTable style={{ width: '100%' }}>
                      <tbody>
                        <tr>
                          <th style={{ width: '30%' }}>Applicable products</th>
                          <td>
                            <CollapsibleContainer
                              text={state.selectedSolution.applicableProducts.join(', ')}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Release Date</th>
                          <td>
                            {state.selectedSolution.releaseDate
                              ? formatDate(state.selectedSolution.releaseDate)
                              : 'No release date'}
                          </td>
                        </tr>
                        <tr>
                          <th>Latest Modified</th>
                          <td>
                            {state.selectedSolution.lastModifiedDate &&
                              formatDate(state.selectedSolution.lastModifiedDate)}
                          </td>
                        </tr>
                        {state.selectedSolution.dryDocking && (
                          <tr>
                            <th>Dry Docking</th>
                            <td>Yes</td>
                          </tr>
                        )}
                      </tbody>
                    </InfoTable>
                  </FlexItem>
                </InfoContainer>
                <FileTableContainer>
                  <FileTable
                    files={state.selectedSolution.files}
                    downloadFile={apiActions.getMaterialFromApi}
                    previewFile={apiActions.getPreview}
                  />
                </FileTableContainer>
                <H2>Related solutions</H2>
                <RelatedSolutionsTableContainer>
                  <RelatedSolutionsTable
                    relatedSolutions={state.selectedSolution.relatedSolutions}
                  />
                </RelatedSolutionsTableContainer>
                <Flex alignHorizontal={FlexPosition.center}>
                  <ViewCountContainer>
                    Total views: <b>{this.state.totalViews}</b>
                    &nbsp;&nbsp; Last 90 days: <b>{this.state.recentViews}</b>
                  </ViewCountContainer>
                </Flex>
                <Flex alignHorizontal={FlexPosition.center} style={{ marginTop: '1em' }}>
                  <Link to="/">
                    <BackButton>&laquo; Return to front page</BackButton>
                  </Link>
                </Flex>
                <ReactTooltip />
              </Flex>
            )}
          </Container>
        )}
      </AppConsumer>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/Offering/Offering.tsx